// src/lib/csp.ts
//
// Content-Security-Policy do admin, em modo BLOQUEIO, com nonce por
// requisição (auditoria pós-incidente de 14/09/2026, H13).
//
// Até aqui a CSP era só Report-Only e liberava 'unsafe-inline' em script-src:
// não impedia nada. O XSS do /atendimento24h (C1, corrigido no #401) rodaria
// mesmo com ela ligada. Agora:
//
//   - script-src aceita só scripts com o nonce desta requisição e, via
//     'strict-dynamic', o que esses scripts carregarem (é assim que o gtm.js
//     puxa as tags do GA4). 'unsafe-inline' e https: ficam como FALLBACK para
//     navegador antigo que não entende nonce/strict-dynamic: navegador moderno
//     ignora os dois quando há nonce (padrão "strict CSP" do Google/OWASP).
//   - O middleware gera o nonce, põe em `x-nonce` e no header CSP da REQUISIÇÃO
//     (o Next 14 lê daí e carimba os próprios scripts inline) e devolve o mesmo
//     CSP na RESPOSTA. O layout raiz lê `x-nonce` para o script do GTM.
//   - style-src mantém 'unsafe-inline': Tailwind, recharts e style={{}} usam
//     atributos inline e CSS não executa código. A proteção que importa está
//     em script-src.
//   - 'wasm-unsafe-eval' libera SÓ compilar e instanciar WebAssembly, nunca
//     eval() de string. O extrato do adicional em PDF é gerado no navegador
//     pelo @react-pdf/renderer, que calcula o layout das páginas com o Yoga
//     compilado em wasm (yoga-layout). Sem esse token o botão Extratos morria
//     em CompileError, e foi o que aconteceu entre 15/09 e 17/09/2026. O vetor
//     do XSS do /atendimento24h precisaria do 'unsafe-eval' de verdade, que
//     segue fora de produção.
//
// Roda no Edge (middleware): só Web Crypto, nada de node:crypto.

const HOSTS_SUPABASE = 'https://*.supabase.co'
const HOSTS_GTM = 'https://www.googletagmanager.com'
const HOSTS_GA = 'https://*.google-analytics.com https://*.analytics.google.com https://stats.g.doubleclick.net'
// As telas públicas pegam as imagens em /publico, no mesmo endereço da página
// ('self'). O endereço do admin fica fora do cabeçalho de propósito: a CSP vai
// junto em toda resposta servida em www.clubesbrazil.com.
const HOSTS_ASSETS_PROPRIOS = 'https://www.clubesbrazil.com'
// Conferência anti-robô do /entrar (lib/turnstile.ts): script e iframe.
const HOSTS_TURNSTILE = 'https://challenges.cloudflare.com'

export function gerarNonce(): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return btoa(bin)
}

export function montarCsp(nonce: string, opts: { dev?: boolean } = {}): string {
  // Next em desenvolvimento injeta eval (HMR/react-refresh). Só em dev.
  const evalDev = opts.dev ? " 'unsafe-eval'" : ''
  return [
    "default-src 'self'",
    `script-src 'nonce-${nonce}' 'strict-dynamic' 'wasm-unsafe-eval' 'unsafe-inline' https:${evalDev}`,
    "style-src 'self' 'unsafe-inline'",
    `img-src 'self' data: blob: ${HOSTS_SUPABASE} ${HOSTS_ASSETS_PROPRIOS} ${HOSTS_GTM} ${HOSTS_GA}`,
    "font-src 'self' data:",
    `connect-src 'self' ${HOSTS_SUPABASE} wss://*.supabase.co ${HOSTS_GTM} ${HOSTS_GA} ${HOSTS_TURNSTILE}`,
    `media-src 'self' blob: ${HOSTS_SUPABASE}`,
    `frame-src ${HOSTS_GTM} ${HOSTS_TURNSTILE}`,
    "worker-src 'self' blob:",
    "object-src 'none'",
    "base-uri 'self'",
    "form-action 'self'",
    "frame-ancestors 'none'",
    'upgrade-insecure-requests',
    'report-uri /api/csp-report',
  ].join('; ')
}

/**
 * Rotas públicas onde o GTM (funil da landing) faz sentido. No backoffice, não.
 * O /entrar ficou de fora até 09/10/2026: os eventos funil_* do formulário iam
 * para um dataLayer que nenhum container lia.
 */
const PREFIXOS_GTM = ['/atendimento24h', '/nps', '/avaliar', '/links', '/entrar']

export function rotaComGtm(pathname: string | null | undefined): boolean {
  if (!pathname) return false
  return PREFIXOS_GTM.some(p => pathname === p || pathname.startsWith(`${p}/`))
}
